<div class="date-range-container" [class.open]="isOpen()">
  <div
    class="input-wrapper"
    (click)="togglePicker()"
    (keydown.enter)="togglePicker()"
    (keydown.space)="togglePicker(); $event.preventDefault()"
    tabindex="0"
    role="button"
    aria-haspopup="dialog"
    [attr.aria-expanded]="isOpen()"
  >
    <div class="display-input">
      <svg
        class="calendar-icon"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
      <span class="value-text" [class.placeholder]="!formattedRange()">
        {{ formattedRange() || placeholder }}
      </span>
      @if (range().start || range().end) {
        <button
          class="clear-btn"
          type="button"
          (click)="clearValue($event)"
          aria-label="Clear date"
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      }
    </div>
  </div>

  @if (isOpen()) {
    <button
      type="button"
      class="picker-backdrop"
      [class.visible]="compactView()"
      (click)="closePicker()"
      aria-label="Close date picker"
    ></button>
    <div
      class="picker-dropdown"
      [class.compact]="compactView()"
      [class.overflow-x-scroll]="allowHorizontalScroll()"
      [ngStyle]="dropdownStyle()"
      (click)="$event.stopPropagation()"
      (keydown)="$event.stopPropagation()"
      role="dialog"
      aria-label="Date range selector"
      tabindex="-1"
    >
      <div class="picker-main">
        <div class="sidebar">
          <div class="sidebar-header">Date Selection</div>
          <div class="preset-list">
            @for (preset of presets; track preset.id) {
              <button
                type="button"
                class="preset-btn"
                [class.active]="activePreset() === preset.id"
                (click)="applyPreset(preset)"
              >
                {{ preset.label }}
              </button>
            }
          </div>
        </div>

        <div class="calendar-content">
          <div class="calendars-wrapper">
            <div class="calendar-pane">
              <div class="calendar-header">
                <button
                  type="button"
                  class="nav-btn"
                  (click)="prevMonth()"
                  aria-label="Previous month"
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <span class="month-label">{{ formatMonth(viewDate()) }}</span>
                <button type="button" class="nav-btn hidden-nav" aria-hidden="true"></button>
              </div>
              <div class="days-grid-header">
                @for (day of weekDays; track day) {
                  <span class="dow">{{ day }}</span>
                }
              </div>
              <div class="days-grid">
                @for (day of calendarDays(viewDate()); track day.date.getTime()) {
                  <button
                    type="button"
                    class="day-btn"
                    [class.current-month]="day.currentMonth"
                    [class.today]="day.isToday"
                    [class.selected]="isDateSelected(day.date)"
                    [class.in-range]="isDateInRange(day.date)"
                    [class.range-start]="isRangeStart(day.date)"
                    [class.range-end]="isRangeEnd(day.date)"
                    [disabled]="isDisabled(day.date)"
                    (click)="onDateClick(day.date)"
                  >
                    <span class="day-number">{{ day.date.getDate() }}</span>
                  </button>
                }
              </div>
            </div>

            <div class="calendar-pane separator">
              <div class="calendar-header">
                <button type="button" class="nav-btn hidden-nav" aria-hidden="true"></button>
                <span class="month-label">{{ formatMonth(nextMonthDate()) }}</span>
                <button type="button" class="nav-btn" (click)="nextMonth()" aria-label="Next month">
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              <div class="days-grid-header">
                @for (day of weekDays; track day) {
                  <span class="dow">{{ day }}</span>
                }
              </div>
              <div class="days-grid">
                @for (day of calendarDays(nextMonthDate()); track day.date.getTime()) {
                  <button
                    type="button"
                    class="day-btn"
                    [class.current-month]="day.currentMonth"
                    [class.today]="day.isToday"
                    [class.selected]="isDateSelected(day.date)"
                    [class.in-range]="isDateInRange(day.date)"
                    [class.range-start]="isRangeStart(day.date)"
                    [class.range-end]="isRangeEnd(day.date)"
                    [disabled]="isDisabled(day.date)"
                    (click)="onDateClick(day.date)"
                  >
                    <span class="day-number">{{ day.date.getDate() }}</span>
                  </button>
                }
              </div>
            </div>
          </div>

          <div class="picker-footer">
            <div class="selected-range-text">
              @if (range().start) {
                {{ range().start | date: 'mediumDate' }}
              }
              @if (range().start && range().end) {
                to {{ range().end | date: 'mediumDate' }}
              }
            </div>
            <div class="footer-actions">
              <button type="button" class="btn-cancel" (click)="closePicker()">Cancel</button>
              <button type="button" class="btn-apply" (click)="apply()" [disabled]="!range().start">
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  }
</div>
